<template>
    <div>
        <b-container fluid>
            <h3 class="mt-3 mb-1">HKTVmall Manual Upload</h3>
            <p class="text-muted">
                Use when the HKTVmall bot is blocked. Download the pick list CSV and the waybill PDFs from the HKTVmall
                merchant portal. Orders and waybills are two separate jobs: do them in either order, or only one of
                them.
            </p>

            <!-- ============ A. ORDERS ============ -->
            <b-card class="mb-3" header="A. Orders — import the pick list CSV">
                <b-row>
                    <b-col md="4">
                        <b-form-group label="Client">
                            <b-form-select
                                size="sm"
                                v-model="order.client_id"
                                :options="client_options"
                                :disabled="order.running"
                            />
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group label="Pick list CSV (one per HKTVmall store)">
                            <b-form-file
                                size="sm"
                                v-model="order.csv_files"
                                accept=".csv"
                                multiple
                                placeholder="pick_list_details_xxx.csv"
                                :disabled="order.running"
                                @input="onOrderCsv"
                            />
                            <small class="text-muted"
                                >Select all CSV files together (Ctrl/Shift-click). Their orders are combined.</small
                            >
                        </b-form-group>
                    </b-col>
                    <b-col md="4">
                        <b-form-group label="Schedule date">
                            <b-form-input
                                size="sm"
                                type="date"
                                v-model="order.schedule_date"
                                :disabled="order.running"
                            />
                            <small class="text-muted"
                                >Pickup date to pack for. Default follows the cron: today before 13:50 (HK),
                                tomorrow from 13:50.</small
                            >
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-alert v-if="order.csv_summary" show variant="info" class="mb-2">
                    <div v-for="f in order.csv_summary.files" :key="f.name">
                        {{ f.name }}: {{ f.rows }} rows, {{ f.orders }} orders, qty {{ f.qty }}
                        <span v-if="f.duplicates_skipped" class="text-danger"
                            >({{ f.duplicates_skipped }} duplicate row(s) already in an earlier file, skipped)</span
                        >
                    </div>
                    <strong
                        >Total: {{ order.csv_summary.rows }} rows, {{ order.csv_summary.orders }} orders,
                        {{ order.csv_summary.waybills }} waybills, qty {{ order.csv_summary.qty }}</strong
                    >
                    <div v-for="d in order.csv_summary.dates" :key="d.date">
                        Pickup {{ d.date }}: {{ d.orders }} order(s)
                        <b-badge v-if="d.date === order.schedule_date" variant="success">schedule date</b-badge>
                    </div>
                </b-alert>
                <b-alert v-if="dateWarning" show variant="warning" class="mb-2">{{ dateWarning }}</b-alert>

                <b-button variant="primary" :disabled="order.running || !canRunOrder" @click="onRunOrder">
                    <b-spinner v-if="order.running" small class="mr-1" />
                    Import orders &amp; run automation
                </b-button>
                <small class="text-muted ml-2"
                    >Imports the orders, then receives / commits them and adds them to the packing queue. Can take a
                    few minutes; keep this page open.</small
                >

                <hktvmall-step-list
                    :steps="order.steps"
                    :summary="order.summary"
                    :running="order.running"
                    retryable
                    @retry="retryOrder"
                />
            </b-card>

            <!-- ============ B. WAYBILLS ============ -->
            <b-card class="mb-3" header="B. Waybills — split the PDFs and upload to FTP">
                <b-form-group label="Waybill PDFs (multiple)">
                    <b-form-file
                        size="sm"
                        v-model="wb.pdf_files"
                        accept=".pdf"
                        multiple
                        placeholder="Choose waybill PDF file(s)"
                        :disabled="wb.running"
                    />
                    <small class="text-muted"
                        >Each page is saved as {waybill no}.pdf on the FTP waybill folder. Nothing is checked against the
                        orders, so this can be done before or after importing them.</small
                    >
                </b-form-group>

                <b-button variant="primary" :disabled="wb.running || !canRunWaybill" @click="onRunWaybill">
                    <b-spinner v-if="wb.running" small class="mr-1" />
                    Split &amp; upload waybills
                </b-button>

                <hktvmall-step-list :steps="wb.steps" :summary="wb.summary" :running="wb.running" />
            </b-card>

            <b-card class="mb-3" header="If something goes wrong">
                <ul class="small mb-0 pl-3">
                    <li>
                        It is safe to run again: orders already imported are skipped and waybill PDFs with the same
                        name on the FTP are overwritten.
                    </li>
                    <li>
                        If an order step fails, the steps before it already finished. Use <em>Retry this step</em>
                        instead of starting over.
                    </li>
                    <li>
                        If the page stops responding or shows "Network Error", the server may still be working. Wait 2
                        minutes, check the orders in Order Master / Trackings, then retry.
                    </li>
                    <li>
                        Still stuck? Send IT the client, the CSV and PDFs, the schedule date and a screenshot of this
                        page.
                    </li>
                </ul>
            </b-card>
        </b-container>
    </div>
</template>

<script>
import HktvmallStepList from "./HktvmallStepList.vue";

export default {
    name: "HktvmallManualUpload",
    components: { HktvmallStepList },
    inject: ["API_HOST"],
    data() {
        return {
            clients: [],
            order: {
                client_id: null,
                csv_files: [],
                schedule_date: "",
                csv_summary: null,
                steps: [],
                summary: null,
                running: false,
                result: null
            },
            wb: {
                pdf_files: [],
                steps: [],
                summary: null,
                running: false,
                result: null
            }
        };
    },
    computed: {
        client_options() {
            return [
                { value: null, text: "-- Select client --" },
                ...this.clients.map(c => ({ value: c.id, text: `${c.client_name} (${c.full_name})` }))
            ];
        },
        dateWarning() {
            const o = this.order;
            if (!o.csv_summary || !o.schedule_date) return "";
            const dates = o.csv_summary.dates.map(d => d.date);
            if (!dates.includes(o.schedule_date)) {
                return `No order in the CSV has pickup date ${o.schedule_date}, so no tracking will be created. Change the schedule date or check the CSV.`;
            }
            if (dates.length > 1) {
                return `The CSV has ${dates.length} pickup dates. Only orders picked up on ${o.schedule_date} are put in tracking. Run again with another schedule date for the rest.`;
            }
            return "";
        },
        canRunOrder() {
            return this.order.client_id && this.order.csv_files.length > 0 && this.order.schedule_date;
        },
        canRunWaybill() {
            return this.wb.pdf_files.length > 0;
        }
    },
    async mounted() {
        this.order.schedule_date = this.defaultScheduleDate();
        await this.loadClients();
    },
    methods: {
        showError(message) {
            this.$eventHub.$emit("show_modal_message", {
                message_content: message,
                variant: "danger"
            });
        },
        async loadClients() {
            const response = await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "hktvmall_manual_clients.php",
                data: { user_id: this.$store.state.user_obj.user_id }
            });
            if (response.data.status !== "success") {
                this.showError(response.data.message);
                return;
            }
            this.clients = response.data.clients;
        },
        clientName(id) {
            const c = this.clients.find(x => x.id == id);
            return c ? c.client_name : "";
        },
        // same rule as cronjob/cron_automate_master.php: before 13:50 HK = today (T+0), from 13:50 = tomorrow (T+1)
        defaultScheduleDate() {
            const parts = {};
            new Intl.DateTimeFormat("en-GB", {
                timeZone: "Asia/Hong_Kong",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false
            })
                .formatToParts(new Date())
                .forEach(p => (parts[p.type] = p.value));
            const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
            const minutes = (parseInt(parts.hour, 10) % 24) * 60 + parseInt(parts.minute, 10);
            if (minutes >= 13 * 60 + 50) {
                date.setUTCDate(date.getUTCDate() + 1);
            }
            return date.toISOString().slice(0, 10);
        },
        // minimal CSV parser (quoted fields, doubled quotes) for the preview only; the server re-reads the file
        parseCsv(text) {
            const rows = [];
            let row = [];
            let field = "";
            let quoted = false;
            for (let i = 0; i < text.length; i++) {
                const c = text[i];
                if (quoted) {
                    if (c === '"' && text[i + 1] === '"') {
                        field += '"';
                        i++;
                    } else if (c === '"') {
                        quoted = false;
                    } else {
                        field += c;
                    }
                } else if (c === '"') {
                    quoted = true;
                } else if (c === ",") {
                    row.push(field);
                    field = "";
                } else if (c === "\n" || c === "\r") {
                    if (c === "\r" && text[i + 1] === "\n") i++;
                    row.push(field);
                    field = "";
                    rows.push(row);
                    row = [];
                } else {
                    field += c;
                }
            }
            if (field !== "" || row.length) {
                row.push(field);
                rows.push(row);
            }
            return rows;
        },
        // pick list CSV -> [{ order, waybill, pickup }] or null when it is not a pick list
        async readPickList(file) {
            const rows = this.parseCsv((await file.text()).replace(/^﻿/, ""));
            const header = (rows[0] || []).map(h => h.trim().toLowerCase());
            const iOrder = header.indexOf("order id");
            const iWaybill = header.indexOf("waybill");
            const iPickup = header.indexOf("pickup date");
            const iSku = header.indexOf("sku id");
            const iQty = header.indexOf("total qty");
            if (iOrder < 0 || iWaybill < 0 || iPickup < 0 || iSku < 0 || iQty < 0) {
                this.showError(`${file.name} does not look like a HKTVmall pick list CSV.`);
                return null;
            }
            return rows
                .slice(1)
                .filter(r => r[iOrder] && r[iWaybill])
                .map(r => ({
                    order: r[iOrder].trim(),
                    waybill: r[iWaybill].trim(),
                    sku: r[iSku].trim(),
                    qty: parseInt(r[iQty], 10) || 0,
                    pickup: r[iPickup].trim()
                }));
        },
        // summarise all selected CSVs; same duplicate rule as the server (a row already in an earlier file is skipped)
        async onOrderCsv(files) {
            this.order.csv_summary = null;
            if (!files || !files.length) return;
            const seen = new Set();
            const all = [];
            const summaries = [];
            for (const file of files) {
                const data = await this.readPickList(file);
                if (!data) {
                    this.order.csv_files = [];
                    return;
                }
                const stat = { name: file.name, rows: 0, orders: new Set(), qty: 0, duplicates_skipped: 0 };
                const fileSeen = new Set();
                data.forEach(r => {
                    const key = `${r.order}|${r.waybill}|${r.sku}`;
                    if (seen.has(key)) {
                        stat.duplicates_skipped++;
                        return;
                    }
                    fileSeen.add(key);
                    all.push(r);
                    stat.rows++;
                    stat.orders.add(`${r.order}-${r.waybill}`);
                    stat.qty += r.qty;
                });
                fileSeen.forEach(k => seen.add(k));
                summaries.push({ ...stat, orders: stat.orders.size });
            }
            const byDate = {};
            all.forEach(r => {
                (byDate[r.pickup] = byDate[r.pickup] || new Set()).add(`${r.order}-${r.waybill}`);
            });
            this.order.csv_summary = {
                files: summaries,
                rows: all.length,
                orders: new Set(all.map(r => `${r.order}-${r.waybill}`)).size,
                waybills: new Set(all.map(r => r.waybill)).size,
                qty: all.reduce((sum, r) => sum + r.qty, 0),
                dates: Object.keys(byDate)
                    .sort()
                    .map(date => ({ date, orders: byDate[date].size }))
            };
        },
        newStep(key, label) {
            return { key, label, state: "waiting", lines: [], log: "", help: [] };
        },
        post(url, data, multipart) {
            return this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url,
                headers: multipart ? { "Content-Type": "multipart/form-data" } : {},
                data,
                timeout: 0
            }).then(r => r.data);
        },
        // the request itself did not finish (network / timeout / server error)
        requestFailed(step, e) {
            step.state = "failed";
            step.lines.push(`The request did not finish: ${e.message}`);
            step.help.push(
                "The server may still be working. Wait 2 minutes and check Order Master / Trackings for this client before retrying."
            );
            step.help.push("Retrying is safe: already imported orders are skipped and FTP files are overwritten.");
        },

        // ===================== A. ORDERS =====================
        async onRunOrder() {
            const o = this.order;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Import ${o.csv_summary ? o.csv_summary.orders : ""} order(s) for ${this.clientName(o.client_id)}, ` +
                    `schedule ${o.schedule_date}?`,
                { title: "Confirm", okTitle: "Run", cancelTitle: "Cancel" }
            );
            if (!confirmed) return;

            o.result = null;
            o.steps = [
                this.newStep("order", "1. Import orders from CSV"),
                this.newStep("automate", "2. Receive, commit & add to packing queue")
            ];
            await this.runOrderFrom(0);
        },
        async retryOrder(index) {
            const o = this.order;
            for (let i = index; i < o.steps.length; i++) {
                const old = o.steps[i];
                this.$set(o.steps, i, this.newStep(old.key, old.label));
            }
            await this.runOrderFrom(index);
        },
        async runOrderFrom(index) {
            const o = this.order;
            o.running = true;
            o.summary = null;
            const runners = [this.runOrderImport, this.runOrderAutomate];
            try {
                for (let i = index; i < runners.length; i++) {
                    const step = o.steps[i];
                    step.state = "running";
                    try {
                        await runners[i](step);
                    } catch (e) {
                        this.requestFailed(step, e);
                    }
                    if (step.state === "failed") break;
                }
            } finally {
                o.running = false;
                this.buildOrderSummary();
            }
        },
        async runOrderImport(step) {
            const o = this.order;
            const form = new FormData();
            form.append("user_id", this.$store.state.user_obj.user_id);
            form.append("client_id", o.client_id);
            form.append("schedule_date", o.schedule_date);
            o.csv_files.forEach(f => form.append("csv[]", f));
            const res = await this.post("hktvmall_manual_order.php", form, true);

            if (res.status !== "success") {
                const msg = res.message || res.error_msg || "Import failed";
                step.state = "failed";
                step.lines.push(msg);
                step.help.push("Nothing after this step ran.");
                if (/missing column|does not look like|empty|no orders/i.test(msg)) {
                    step.help.push(
                        "Download the Pick List Details CSV again from the HKTVmall merchant portal. Do not edit or re-save it in Excel."
                    );
                } else if (/another HKTVmall process/i.test(msg)) {
                    step.help.push(
                        "Another import is running. Wait a few minutes and run again. If it is still blocked after 15 minutes, ask IT to clear the stuck row in tbl_hktvmall_log."
                    );
                } else if (/Invalid Courier/i.test(msg)) {
                    step.help.push("The HKTVMALL courier is missing in the courier list. Ask IT to add it. Nothing was imported.");
                } else if (/store name/i.test(msg)) {
                    step.help.push("This client is not set up for HKTVmall. Ask IT to set its HKTVmall store name.");
                } else if (/Permission denied/i.test(msg)) {
                    step.help.push("Your account is not allowed to use this page.");
                } else {
                    step.help.push("Run again. If it keeps failing, send IT this message together with the CSV.");
                }
                return;
            }

            o.result = res;
            const mismatch = Object.keys(res.schedule_mismatch);
            const expected_tracking = res.orders - mismatch.length;
            (res.files || []).forEach(f =>
                step.lines.push(`${f.name}: ${f.rows} rows, ${f.orders} orders, qty ${f.qty}`)
            );
            step.lines.push(`Total qty in the CSV(s): ${res.total_qty}.`);
            step.lines.push(
                `${res.orders} order(s) in the CSV: ${res.orders - res.already_imported} new, ${res.already_imported} already imported (skipped).`
            );
            step.lines.push(
                `Now in Order Master: ${res.in_order_master} of ${res.orders}. In Tracking: ${res.in_tracking} of ${expected_tracking} with pickup date ${res.schedule_date}.`
            );
            let warn = false;
            if (mismatch.length) {
                warn = true;
                step.lines.push(`Different pickup date, not put in Tracking: ${mismatch.join(", ")}`);
                step.help.push(
                    `${mismatch.length} order(s) have another pickup date. They are in Order Master but not in Tracking. Run this page again with their pickup date when it is their day.`
                );
            }
            if (res.in_order_master < res.orders) {
                warn = true;
                step.help.push(
                    `${res.orders - res.in_order_master} order(s) were not imported (invalid date or quantity in the CSV). Check those rows in the CSV and ask IT if unsure.`
                );
            }
            if (res.in_tracking < expected_tracking) {
                warn = true;
                step.help.push(
                    `${expected_tracking - res.in_tracking} order(s) with pickup date ${res.schedule_date} are not in Tracking. Ask IT to check import log #${res.hktvmall_log_id}.`
                );
            }
            if (res.already_imported === res.orders) {
                warn = true;
                step.help.push("Every order was already imported. If you expected new orders, check you picked the right CSV.");
            }
            if (res.import_output) {
                warn = true;
                step.log = res.import_output;
                step.help.push("The import printed a message (below). Check the counts above; tell IT if they look wrong.");
            }
            step.state = warn ? "warning" : "done";
        },
        async runOrderAutomate(step) {
            const o = this.order;
            const res = await this.post("hktvmall_manual_automate.php", {
                user_id: this.$store.state.user_obj.user_id,
                client_id: o.client_id,
                schedule_date: o.schedule_date
            });
            if (res.status !== "success") {
                step.state = "failed";
                step.lines.push(res.message || "Automation failed");
                step.help.push("The orders from step 1 are already imported. Click Retry this step.");
                step.help.push("If it fails again, the orders can be received / committed from Order Master. Tell IT.");
                return;
            }
            step.log = res.log;
            step.state = "done";
            if (/Invalid SKU/i.test(res.log)) {
                step.state = "warning";
                step.help.push(
                    "Some orders have SKUs that are not in the Product Master (see the alert email). They are not committed. Add the SKU, then click Retry this step."
                );
            }
            if (/No orders found for receiving/i.test(res.log)) {
                step.state = "warning";
                step.help.push("There was nothing to receive. Normal if the orders were already received earlier.");
            }
            if (/No tracking IDs found/i.test(res.log)) {
                step.state = "warning";
                step.help.push(
                    `Nothing was added to the packing queue. Check that the schedule date (${o.schedule_date}) matches the pickup date of the orders, then click Retry this step.`
                );
            }
            if (!/Round \d+ completed/i.test(res.log)) {
                step.state = "warning";
                step.help.push("The log does not end with 'Round completed'. Check the log above and click Retry this step.");
            }
        },
        buildOrderSummary() {
            const o = this.order;
            const states = o.steps.map(s => s.state);
            const failed = states.indexOf("failed");
            const lines = [];
            if (o.result) {
                lines.push(
                    `Orders: ${o.result.in_order_master} of ${o.result.orders} in Order Master, ${o.result.in_tracking} in Tracking.`
                );
            }
            if (failed >= 0) {
                o.summary = {
                    variant: "danger",
                    title: `Stopped at step ${failed + 1}. Read "What to do" under that step.`,
                    lines
                };
            } else if (states.includes("warning")) {
                o.summary = { variant: "warning", title: "Finished, but please check the warnings below.", lines };
            } else if (states.every(st => st === "done")) {
                o.summary = { variant: "success", title: "Orders done.", lines };
            } else {
                o.summary = null;
            }
        },

        // ===================== B. WAYBILLS =====================
        async onRunWaybill() {
            const w = this.wb;
            const confirmed = await this.$bvModal.msgBoxConfirm(
                `Split ${w.pdf_files.length} PDF file(s) and upload the waybills to the FTP?`,
                { title: "Confirm", okTitle: "Upload", cancelTitle: "Cancel" }
            );
            if (!confirmed) return;

            w.result = null;
            w.summary = null;
            w.steps = [this.newStep("waybill", "Split waybill PDFs & upload to FTP")];
            w.running = true;
            const step = w.steps[0];
            step.state = "running";
            try {
                await this.runWaybillUpload(step);
            } catch (e) {
                this.requestFailed(step, e);
            } finally {
                w.running = false;
                this.buildWaybillSummary();
            }
        },
        async runWaybillUpload(step) {
            const w = this.wb;
            const form = new FormData();
            form.append("user_id", this.$store.state.user_obj.user_id);
            w.pdf_files.forEach(f => form.append("pdfs[]", f));
            const res = await this.post("hktvmall_manual_waybill.php", form, true);

            if (res.uploaded === undefined) {
                step.state = "failed";
                step.lines.push(res.message || "Waybill upload failed");
                if (res.debug) step.log = res.debug;
                if (/could not read/i.test(res.message || "")) {
                    step.help.push(
                        "Download that PDF again from HKTVmall and upload again. If the PDF opens fine, ask IT (server PDF tool)."
                    );
                } else if (/FTP/i.test(res.message || "")) {
                    step.help.push("The FTP server could not be reached. Click Split & upload again; if it persists ask IT.");
                } else {
                    step.help.push("Check the PDF files and upload again.");
                }
                return;
            }

            w.result = res;
            step.lines.push(`${res.uploaded.length} waybill(s) uploaded to FTP. A notification email was sent.`);
            const issues = [
                [
                    "failed to upload",
                    res.failed,
                    "FTP upload failed for these. Click Split & upload again (files are overwritten, so no duplicates)."
                ],
                [
                    "pages without a waybill no. (skipped)",
                    res.unreadable_pages,
                    "Usually a summary page and harmless. If it is a real waybill, send IT the PDF."
                ],
                [
                    "found more than once (last one used)",
                    res.duplicated,
                    "The same waybill is in more than one PDF page. Harmless."
                ]
            ];
            let warn = res.status !== "success";
            issues.forEach(([text, list, help]) => {
                if (list && list.length) {
                    warn = true;
                    step.lines.push(`${list.length} ${text}: ${list.join(", ")}`);
                    step.help.push(help);
                }
            });
            step.state = res.status !== "success" ? "failed" : warn ? "warning" : "done";
        },
        buildWaybillSummary() {
            const w = this.wb;
            const state = w.steps[0].state;
            const lines = [];
            if (w.result && w.result.uploaded) {
                lines.push(`Waybills: ${w.result.uploaded.length} uploaded to FTP.`);
            }
            if (state === "failed") {
                w.summary = { variant: "danger", title: 'Waybill upload failed. Read "What to do" below.', lines };
            } else if (state === "warning") {
                w.summary = { variant: "warning", title: "Finished, but please check the warnings below.", lines };
            } else if (state === "done") {
                w.summary = { variant: "success", title: "Waybills done.", lines };
            } else {
                w.summary = null;
            }
        }
    }
};
</script>



// WEBPACK FOOTER //
// src/components/HktvmallManualUpload.vue